import {
  api,
  type components,
  getAccessToken,
  resetTokens,
  setAccessToken,
  setRefreshToken,
  subscribeToken,
} from "@awtoban/shared";
import * as React from "react";

type SessionUser = components["schemas"]["SessionUser"];

interface AuthContextValue {
  user: SessionUser | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = React.createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = React.useState<SessionUser | null>(null);
  const [loading, setLoading] = React.useState(true);

  const refreshUser = React.useCallback(async () => {
    const token = getAccessToken();
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    const { data } = await api.GET("/api/v1/auth/me", {});
    setUser((data as SessionUser | undefined) ?? null);
    setLoading(false);
  }, []);

  React.useEffect(() => {
    void refreshUser();
    return subscribeToken(() => void refreshUser());
  }, [refreshUser]);

  const login = React.useCallback(
    async (username: string, password: string) => {
      const { data, error } = await api.POST("/api/v1/auth/login", {
        body: { username, password } as never,
      });
      if (error || !data) throw new Error("Login failed");
      const tokens = data as { access?: string; refresh?: string };
      if (!tokens.access) throw new Error("Login response missing access token");
      setAccessToken(tokens.access);
      setRefreshToken(tokens.refresh ?? null);
      await refreshUser();
    },
    [refreshUser],
  );

  const logout = React.useCallback(() => {
    resetTokens();
    setUser(null);
  }, []);

  const value = React.useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      isAuthenticated: user !== null,
      login,
      logout,
    }),
    [user, loading, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
